import type { Store, StoreRole } from "@hobbyshopify/backend-client";
import { useEffect, useLayoutEffect } from "react";
import { Link, NavLink, Outlet, useLocation, useMatch } from "react-router-dom";
import { Obs, useObserver, useSubtree } from "subtree.js/react";
import { hasCapability } from "../core/primitives/capabilities";
import { can } from "../core/primitives/store-role";
import { workspaceLabel } from "../core/primitives/workspace";
import { SetupChecklist } from "../sections/setup-checklist/setup-checklist";
import { ShellActions, ShellState } from "../shell/shell-model";
import { barUnder, customerPathFor, inventoryPathFor, tabHref, tabOf, tabsFor } from "../shell/shell-tabs";
import { TabBar } from "./TabBar";

/**
 * The count of carts waiting on the merchant. Accent-filled, because this is the
 * one number in the shell that means "someone is standing at your counter".
 */
function NavBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="ml-2 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-ink">
      {count}
    </span>
  );
}

const navCls = ({ isActive }: { isActive: boolean }) =>
  `block rounded-btn px-3 py-2.5 text-sm font-medium transition-colors ${
    isActive ? "bg-surface text-accent" : "text-muted hover:bg-surface hover:text-white"
  }`;

function LockIcon() {
  return (
    <svg aria-hidden="true" focusable="false" className="size-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 2a4 4 0 0 0-4 4v2H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1h-1V6a4 4 0 0 0-4-4Zm2 6V6a2 2 0 1 0-4 0v2h4Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/** A feature HobbyScan has not switched on: still listed, so it can be found, and its page asks for it. */
function LockedLabel({ children }: { children: string }) {
  return (
    <span className="flex items-center justify-between gap-2 opacity-60" title="Not switched on for this shop">
      {children}
      <LockIcon />
      <span className="sr-only"> (not switched on)</span>
    </span>
  );
}

/** The shop's logo, else its initial on the accent. */
function ShopBadge({ store, className }: { store: Store; className: string }) {
  if (store.logoUrl)
    return (
      <img
        src={store.logoUrl}
        alt=""
        className={`${className} shrink-0 rounded-thumb border border-line object-cover`}
      />
    );
  return (
    <span
      className={`${className} flex shrink-0 items-center justify-center rounded-thumb bg-accent font-semibold text-ink`}
    >
      {(store.name ?? "Your shop").charAt(0).toUpperCase()}
    </span>
  );
}

/** A Manager's settings open on the admins, the part of them a Manager may change. */
const settingsPath = (storeId: string, role: StoreRole | null) =>
  role === "MANAGER" ? `/stores/${storeId}/settings?tab=admins` : `/stores/${storeId}/settings`;

/** The shop × location the app is working at, linking to the shop's settings. */
function ShopIdentity() {
  const state = useSubtree(ShellState);
  const observer = useObserver();
  const selectedStore = observer.watch(state.selectedStore);
  const location = observer.watch(state.selectedLocation);
  const role = observer.watch(state.role);
  if (!selectedStore) return null;

  const name = workspaceLabel(selectedStore, location);
  const badge = <ShopBadge store={selectedStore} className="h-6 w-6 text-[11px]" />;

  // A Staff member has nowhere this badge could send them, so it stops being a link.
  if (role === "STAFF") {
    return (
      <span className="flex min-w-0 items-center gap-2 rounded-btn px-2 py-1.5" title={name}>
        {badge}
        <span className="truncate text-sm font-medium text-white">{name}</span>
      </span>
    );
  }

  return (
    <Link
      to={settingsPath(selectedStore.id, role)}
      className="flex min-w-0 items-center gap-2 rounded-btn px-2 py-1.5 transition-colors hover:bg-surface"
      title={`${name} — settings`}
    >
      {badge}
      <span className="truncate text-sm font-medium text-white">{name}</span>
    </Link>
  );
}

const GEAR =
  "M10.21 2.77 L13.79 2.77 L14.04 5.10 L15.44 5.67 L17.26 4.21 L19.79 6.74 L18.33 8.56 L18.90 9.96 L21.23 10.21 L21.23 13.79 L18.90 14.04 L18.33 15.44 L19.79 17.26 L17.26 19.79 L15.44 18.33 L14.04 18.90 L13.79 21.23 L10.21 21.23 L9.96 18.90 L8.56 18.33 L6.74 19.79 L4.21 17.26 L5.67 15.44 L5.10 14.04 L2.77 13.79 L2.77 10.21 L5.10 9.96 L5.67 8.56 L4.21 6.74 L6.74 4.21 L8.56 5.67 L9.96 5.10 Z";

/** iOS's gearshape: filled while Settings is the page. */
function GearIcon({ filled }: { filled: boolean }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" aria-hidden="true" strokeLinejoin="round">
      {filled ? (
        <path fill="currentColor" fillRule="evenodd" d={`${GEAR} M15 12a3 3 0 1 0-6 0a3 3 0 1 0 6 0Z`} />
      ) : (
        <g fill="none" stroke="currentColor" strokeWidth={1.75}>
          <path d={GEAR} />
          <circle cx="12" cy="12" r="3" />
        </g>
      )}
    </svg>
  );
}

/**
 * A phone's header, as iOS's shop mode: the shop and its counter on two lines (a way back to the picker when there is
 * more than one to pick), the gear for Settings, and Log out where iOS has its way out of shop mode.
 */
export function PhoneHeader() {
  const state = useSubtree(ShellState);
  const actions = useSubtree(ShellActions);
  const observer = useObserver();
  const store = observer.watch(state.selectedStore);
  const location = observer.watch(state.selectedLocation);
  const role = observer.watch(state.role);
  const canSwitch = observer.watch(state.canSwitchWorkspace);
  const onSettings = useMatch("/stores/:storeId/settings") !== null;
  // Above a tab's pages it stays while they scroll, as iOS's does; a sheet's own header takes its place.
  const tabbed = barUnder(useLocation().pathname);

  const identity = store && (
    <span className="flex min-w-0 items-center gap-2">
      <ShopBadge store={store} className="size-[26px] text-xs" />
      <span className="flex min-w-0 flex-col text-left">
        <span className="truncate text-[15px] leading-5 font-semibold text-white">{store.name ?? "Your shop"}</span>
        {location?.name && <span className="truncate text-xs leading-4 text-muted">{location.name}</span>}
      </span>
    </span>
  );

  return (
    <header
      className={`${tabbed ? "sticky top-[var(--on-air,0px)]" : "relative"} z-20 flex h-14 shrink-0 items-center gap-3 border-b border-line bg-app-elevated pr-[max(1rem,env(safe-area-inset-right))] pl-[max(1rem,env(safe-area-inset-left))] sm:hidden`}
    >
      {canSwitch ? (
        <button
          type="button"
          onClick={actions.switchWorkspace}
          aria-label="Switch shop or location"
          className="flex min-w-0 items-center gap-1.5 rounded-btn"
        >
          {identity}
          <svg
            width={12}
            height={12}
            viewBox="0 0 12 12"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-muted"
          >
            <path d="M3.5 4.5L6 2l2.5 2.5M3.5 7.5L6 10l2.5-2.5" />
          </svg>
        </button>
      ) : (
        identity
      )}
      {store && role !== "STAFF" && (
        <Link
          to={settingsPath(store.id, role)}
          aria-label="Settings"
          aria-current={onSettings ? "page" : undefined}
          className={`flex size-9 shrink-0 items-center justify-center rounded-btn transition-colors ${
            onSettings ? "text-accent" : "text-muted hover:text-white"
          }`}
        >
          <GearIcon filled={onSettings} />
        </Link>
      )}
      <span className="min-w-2 flex-1" />
      <Obs>
        {(ref) => {
          const signOut = ref.disableUntilCompleted(actions.signOut);
          return (
            <button
              type="button"
              onClick={signOut}
              disabled={signOut === undefined}
              className="shrink-0 text-sm font-semibold text-accent"
            >
              Log out
            </button>
          );
        }}
      </Obs>
    </header>
  );
}

/** A phone's tabs, under every page of a tab and under Settings; a page iOS shows as a sheet goes without. */
export function PhoneTabs() {
  const state = useSubtree(ShellState);
  const observer = useObserver();
  const role = observer.watch(state.role);
  const openCarts = observer.watch(state.pendingCarts);
  const onAir = observer.watch(state.onAir);
  const liveNow = observer.watch(state.liveNow);
  const inventoryPath = observer.watch(state.inventoryPath);
  const customerPath = observer.watch(state.customerPath);
  const here = useLocation();
  if (!barUnder(here.pathname)) return null;
  return (
    <TabBar
      tabs={tabsFor(role)}
      current={tabOf(here.pathname)}
      hrefOf={(tab) => tabHref(tab, here, inventoryPath, customerPath)}
      openCarts={openCarts}
      live={onAir ? "on-air" : liveNow ? "live" : null}
    />
  );
}

function SwitchWorkspaceButton() {
  const state = useSubtree(ShellState);
  const actions = useSubtree(ShellActions);
  const observer = useObserver();
  if (!observer.watch(state.canSwitchWorkspace)) return null;
  return (
    <button
      type="button"
      onClick={actions.switchWorkspace}
      className="shrink-0 rounded-btn px-2 py-2 text-[13px] font-medium text-muted transition-colors hover:bg-surface hover:text-white"
    >
      Switch
    </button>
  );
}

/**
 * Live, from anywhere in the app. Two different facts share this one control:
 * **on air** is this device's camera publishing; **live** is the shop having a
 * show running, which can be a co-host on another device entirely.
 */
function LiveTopBarButton() {
  const state = useSubtree(ShellState);
  const observer = useObserver();
  const onAir = observer.watch(state.onAir);
  const liveNow = observer.watch(state.liveNow);
  const role = observer.watch(state.role);
  const capabilities = observer.watch(state.capabilities);
  const lit = onAir || liveNow;

  // A null role means the shop has not resolved yet; treated as hosting-capable
  // rather than flashing the button on and off once the real role arrives.
  if (role !== null && !can(role, "hostLive")) return null;
  // Switched off, the nav's locked entry is the way in; here it would only be noise.
  if (hasCapability(capabilities, "LIVE_BREAKS") === false && !onAir) return null;

  return (
    <Link
      to="/live"
      className={`flex shrink-0 items-center gap-1.5 rounded-btn px-2.5 py-2 text-sm font-medium transition-colors ${
        lit ? "text-danger hover:bg-surface" : "text-muted hover:bg-surface hover:text-white"
      }`}
      title={onAir ? "You are on air" : liveNow ? "A show is live in your shop" : "Live breaks"}
    >
      {lit && (
        // Not colour alone: the dot pulses, so the state survives a bright room and colour blindness.
        <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-current" />
      )}
      Live
      {lit && <span className="sr-only">{onAir ? " — you are on air" : " — a show is live"}</span>}
    </Link>
  );
}

export function NavLinks() {
  const state = useSubtree(ShellState);
  const observer = useObserver();
  const selectedStore = observer.watch(state.selectedStore);
  const onAir = observer.watch(state.onAir);
  const pendingCustomerCarts = observer.watch(state.pendingCarts);
  const role = observer.watch(state.role);
  const capabilities = observer.watch(state.capabilities);
  // A null role means the shop has not resolved yet; treated as hosting-capable
  // rather than flashing Live/Settings on and off once the real role arrives.
  const canHostLive = role === null || can(role, "hostLive");
  const showSettings = role !== "STAFF";
  // Unknown capabilities read as on, for the same reason.
  const posLocked = hasCapability(capabilities, "POINT_OF_SALE") === false;
  const liveLocked = hasCapability(capabilities, "LIVE_BREAKS") === false;
  // Stock and review are one Inventory section, as on iOS: the nav goes back to where it was, or lets it choose.
  const onReview = useMatch("/drafts/*") !== null;
  const onInventory = useMatch("/inventory") !== null;
  const onCards = useMatch("/cards/*") !== null;
  const inventoryPath = observer.watch(state.inventoryPath);
  const customerPath = observer.watch(state.customerPath);
  const onInventoryPane = onCards || onReview || onInventory;
  const here = useLocation();

  return (
    <>
      {/* One entry for the counter's three pages, lit on each, and kept on the one shown as the tab is. */}
      <NavLink to={tabHref("customer", here, inventoryPath, customerPath)} className={navCls}>
        {posLocked ? (
          <LockedLabel>Customer</LockedLabel>
        ) : (
          <span className="flex items-center">
            Customer
            <NavBadge count={pendingCustomerCarts} />
          </span>
        )}
      </NavLink>

      {/* Everything above is the customer's side of the counter. Below is the shop's own. */}
      <p className="eyebrow px-3 pb-1 pt-4">Shop</p>

      {/* Sealed product keeps an entry of its own here, so Inventory goes to Stock or Review, and is the one lit. */}
      <Link
        to={inventoryPath === null || inventoryPath === "/sealed" ? "/inventory" : inventoryPath}
        aria-current={onInventoryPane ? "page" : undefined}
        className={navCls({ isActive: onInventoryPane })}
      >
        Inventory
      </Link>
      <NavLink to="/sealed" className={navCls}>
        Sealed product
      </NavLink>
      <NavLink to="/events" className={navCls}>
        Shows
      </NavLink>
      {/* Not `end`: /live/start and /live/shows/:id are all this section. */}
      {canHostLive && (
        <>
          <NavLink to="/live" className={navCls}>
            {liveLocked ? (
              <LockedLabel>Live breaks</LockedLabel>
            ) : (
              <span className="flex items-center justify-between gap-2">
                Live breaks
                {onAir && (
                  <span className="flex items-center gap-1 text-[11px] font-semibold text-danger">
                    <span className="size-1.5 animate-pulse rounded-full bg-current" />
                    On air
                  </span>
                )}
              </span>
            )}
          </NavLink>
          <NavLink to="/shipping" className={navCls}>
            Shipping
          </NavLink>
        </>
      )}
      {selectedStore && showSettings && (
        <>
          <hr className="my-2 border-line" />
          <NavLink to={`/stores/${selectedStore.id}/settings`} className={navCls}>
            Settings
          </NavLink>
        </>
      )}
    </>
  );
}

/** Keeps where Inventory and Customer are, for the nav to go back to. */
function useTabMemory(rememberInventory: (path: string) => void, rememberCustomer: (path: string) => void) {
  const { pathname, search } = useLocation();
  useEffect(() => {
    const inventory = inventoryPathFor(pathname, search);
    if (inventory !== null) rememberInventory(inventory);
    const customer = customerPathFor(pathname, search);
    if (customer !== null) rememberCustomer(customer);
  }, [pathname, search, rememberInventory, rememberCustomer]);
}

export function AppLayout() {
  const state = useSubtree(ShellState);
  const actions = useSubtree(ShellActions);
  const observer = useObserver();
  useTabMemory(actions.rememberInventory, actions.rememberCustomer);
  // The on-air bar is fixed to the top of the viewport, from above the router.
  // Without this the shell keeps its own top edge there and the bar covers the header.
  const onAir = observer.watch(state.onAir);
  // Settings is where the same connection is shown in full.
  const onSettings = useMatch("/stores/:storeId/settings") !== null;
  // A phone's tab bar is fixed to the bottom: the page leaves its height, and its own fixed pieces sit above it. The
  // document scrolls, so the root carries what the bars cover (`index.css`), and the focus is never scrolled under one.
  const tabbed = barUnder(useLocation().pathname);
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.toggleAttribute("data-tabbed", tabbed);
    return () => root.removeAttribute("data-tabbed");
  }, [tabbed]);

  return (
    <div className={`flex min-h-screen flex-col bg-app ${onAir ? "pt-10" : ""}`}>
      <PhoneHeader />
      <header className="relative z-20 hidden h-16 shrink-0 items-center border-b border-line bg-app-elevated pr-[max(1.5rem,env(safe-area-inset-right))] pl-[max(1.5rem,env(safe-area-inset-left))] sm:flex">
        <div className="flex w-full items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {/* The wordmark carries the accent on "Scan", which is how the iOS app writes it. */}
            <span className="text-lg font-bold text-white">
              Hobby<span className="text-accent">Scan</span>.Pro
            </span>
            <LiveTopBarButton />
          </div>

          <div className="flex min-w-0 items-center gap-1">
            <ShopIdentity />
            <SwitchWorkspaceButton />
            <Obs>
              {(ref) => {
                const signOut = ref.disableUntilCompleted(actions.signOut);
                return (
                  <button
                    type="button"
                    onClick={signOut}
                    disabled={signOut === undefined}
                    className="shrink-0 rounded-btn px-3 py-2 text-sm font-medium text-muted transition-colors hover:bg-surface hover:text-white"
                  >
                    Log out
                  </button>
                );
              }}
            </Obs>
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* As tall as the page and partly on screen wherever it is: never the browser's scroll anchor, or a list's
            rows would not be held in place when something above them goes. */}
        <aside className="hidden w-52 shrink-0 border-r border-line bg-app-elevated pl-[env(safe-area-inset-left)] [overflow-anchor:none] sm:block">
          <nav className="space-y-0.5 p-3">
            <NavLinks />
          </nav>
        </aside>

        <main className="flex-1 overflow-auto pr-[env(safe-area-inset-right)] pb-[var(--tab-bar,0px)] max-sm:pl-[env(safe-area-inset-left)]">
          {!onSettings && (
            <div className="mx-auto max-w-4xl px-4 pt-6 empty:hidden sm:px-6 lg:px-8">
              <SetupChecklist />
            </div>
          )}
          <Outlet />
        </main>
      </div>
      <PhoneTabs />
    </div>
  );
}
